import { AppModule } from "@/store/modules/app";
import * as turf from "@turf/helpers";
import length from "@turf/length";
import lineSliceAlong from "@turf/line-slice-along";
import { loadModules } from "esri-loader";

interface QueueLength {
  id: string;
  length: number;
  rtLength: number;
}

export default class LaneQueueLength {
  private static intances: Map<string, LaneQueueLength>;

  public static getInstance(view: __esri.SceneView) {
    const id = view.container.id;
    if (!LaneQueueLength.intances) {
      LaneQueueLength.intances = new Map();
    }
    let instance = LaneQueueLength.intances.get(id);
    if (!instance) {
      instance = new LaneQueueLength(view);
      LaneQueueLength.intances.set(id, instance);
    }
    return instance;
  }

  private initialized = false;

  private view: __esri.SceneView;
  private queueLayer!: __esri.GraphicsLayer;
  private Graphic!: __esri.GraphicConstructor;
  private Polyline!: __esri.PolylineConstructor;
  private Color!: __esri.ColorConstructor;
  /** 所有车道中心线 */
  private allLaneLines: Map<string, __esri.Polyline> = new Map();
  /** 每个路口上的排队方块，便于删除 */
  private queueGraphics: Map<string, Array<__esri.Graphic>> = new Map();
  private readonly maxQueueLength = 100;
  private readonly queueSegLength = 0.5;
  private readonly queueGapLength = 0.5;

  private readonly refreshInterval = 5000;
  private refreshHandle!: number;

  private constructor(view: __esri.SceneView) {
    this.view = view;
  }

  private async initialize() {
    type MapModules = [
      typeof import("esri/Color"),
      typeof import("esri/Graphic"),
      typeof import("esri/geometry/Polyline"),
      typeof import("esri/layers/GraphicsLayer"),
      typeof import("esri/layers/FeatureLayer"),
      typeof import("esri/geometry/support/webMercatorUtils")
    ];
    const [
      Color,
      Graphic,
      Polyline,
      GraphicsLayer,
      FeatureLayer,
      webMercatorUtils,
    ] = await (loadModules([
      "esri/Color",
      "esri/Graphic",
      "esri/geometry/Polyline",
      "esri/layers/GraphicsLayer",
      "esri/layers/FeatureLayer",
      "esri/geometry/support/webMercatorUtils",
    ]) as Promise<MapModules>);
    this.Color = Color;
    this.Graphic = Graphic;
    this.Polyline = Polyline;

    this.queueLayer = new GraphicsLayer();
    this.view.map.add(this.queueLayer);

    const laneLayer = new FeatureLayer({
      url: `http://${AppModule.appConfig.gisServer}/arcgis/rest/services/Unicron/ShangHai_Road/MapServer/7`,
      outFields: ["*"],
    });
    const features = (await laneLayer.queryFeatures(laneLayer.createQuery()))
      .features;
    features.forEach((feature: any) => {
      let laneLine = new Polyline({
        paths: feature.geometry.paths,
      });
      const id: string = feature.attributes["FEATUREID"];
      this.allLaneLines.set(id, laneLine);
    });

    this.initialized = true;
  }

  public async showQueueLength(crossId: string) {
    if (!this.initialized) {
      await this.initialize();
    }

    // 生成最大排队长度
    const queueLengthData: Array<QueueLength> = [];
    for (let id of this.allLaneLines.keys()) {
      if (id.includes(crossId)) {
        queueLengthData.push({
          id,
          length: this.randomLength(),
          rtLength: 0,
        });
      }
    }

    this.generateQueueLine(crossId, queueLengthData);
    this.refreshHandle = setInterval(() => {
      this.generateQueueLine(crossId, queueLengthData);
    }, this.refreshInterval);
  }

  private generateQueueLine(crossId: string, queueLengthData: QueueLength[]) {
    const removeGraphics = this.queueGraphics.get(crossId);
    if (removeGraphics) {
      this.queueLayer.removeMany(removeGraphics);
    }

    const graphics: Array<__esri.Graphic> = [];
    this.queueGraphics.set(crossId, graphics);

    for (let data of queueLengthData) {
      data.rtLength = this.randomLength(10);

      const laneLine = this.allLaneLines.get(data.id);
      if (!laneLine) {
        continue;
      }

      const laneLineString = turf.lineString(laneLine.paths[0]);
      const laneLength = length(laneLineString, { units: "meters" });

      // 历史最大排队长度
      const maxLength = Math.min(data.length, laneLength, this.maxQueueLength);

      const queueLine = lineSliceAlong(laneLineString, 1, maxLength, {
        units: "meters",
      });
      const path = queueLine.geometry.coordinates;
      const queueGraphic = new this.Graphic({
        geometry: {
          type: "polyline",
          paths: [path],
          spatialReference: { wkid: 4326 },
        } as any,
        symbol: this.getQueueSymbol(maxLength),
        attributes: { crossId },
      });
      this.queueLayer.add(queueGraphic);
      graphics.push(queueGraphic);

      // 实时排队长度
      const rtLength = Math.min(data.rtLength, laneLength, this.maxQueueLength);

      let drawedLength = 1;
      while (drawedLength < rtLength) {
        const subLine = lineSliceAlong(
          laneLineString,
          drawedLength,
          drawedLength + this.queueSegLength,
          { units: "meters" }
        );
        const path = subLine.geometry.coordinates;
        const subLaneLine = new this.Polyline({
          paths: [path],
          spatialReference: { wkid: 4326 },
        });
        drawedLength += this.queueSegLength + this.queueGapLength;
        const subLaneLineGraphic = new this.Graphic({
          geometry: subLaneLine,
          symbol: {
            type: "line-3d",
            symbolLayers: [
              {
                type: "path",
                profile: "quad",
                width: 1.5,
                height: 0.2,
                material: { color: "#eeeeee" },
                cap: "butt",
                profileRotation: "heading",
                castShadows: false,
              },
            ],
          } as any,
          attributes: {
            crossId,
          },
        });
        this.queueLayer.add(subLaneLineGraphic);
        graphics.push(subLaneLineGraphic);
      }
    }
  }

  private getQueueSymbol(queueLength: number, step?: number): __esri.Symbol {
    let color: __esri.Color;
    if (queueLength < 70) {
      // 绿色
      if (step) {
        color = this.Color.blendColors(
          new this.Color([255, 255, 255, 0]),
          new this.Color([76, 175, 80, 0.8]),
          step
        );
      } else {
        color = new this.Color([0, 255, 0, 0.4]);
      }
    } else if (queueLength < 90) {
      // 黄色
      if (step) {
        color = this.Color.blendColors(
          new this.Color([255, 255, 255, 0]),
          new this.Color([255, 235, 59, 0.8]),
          step
        );
      } else {
        color = new this.Color([255, 255, 0, 0.4]);
      }
    } else {
      // 红色
      if (step) {
        color = this.Color.blendColors(
          new this.Color([255, 255, 255, 0]),
          new this.Color([244, 67, 54, 0.8]),
          step
        );
      } else {
        color = new this.Color([255, 0, 0, 0.4]);
      }
    }
    const symbol = {
      type: "line-3d",
      symbolLayers: [
        {
          type: "path",
          profile: "quad",
          width: 2,
          height: 0.1,
          material: { color },
          cap: "butt",
          profileRotation: "heading",
          castShadows: false,
        },
      ],
    } as any;
    return symbol;
  }

  /** 50-100的随机数 */
  private randomLength(max?: number): number {
    const maxValue = max || 50;
    return Math.round(Math.random() * 50) + maxValue;
  }

  public hideQueueLength(crossId: string) {
    const graphics = this.queueGraphics.get(crossId);
    if (graphics) {
      this.queueLayer.removeMany(graphics);
    }
    if (this.refreshHandle) {
      clearInterval(this.refreshHandle);
    }
  }
}
